import React, { useState } from 'react';
import { Clock, ChevronRight, AlertCircle, CheckCircle2, Package, Truck, Globe, Smartphone, User, XCircle, Search, Hash, Phone, Calendar } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export type OrderStatus = 'Pending' | 'Accepted' | 'Picking' | 'Searching for Driver' | 'Driver Assigned' | 'Out for Delivery' | 'Delivered' | 'Delivery Attempt Failed' | 'Held' | 'Cancelled' | 'Ready for Pickup' | 'Return Requested' | 'Waiting Return Confirmation' | 'Refunded';
export type Channel = 'Website' | 'Aggregator';
export type PaymentType = 'COD' | 'Visa' | 'Mada' | 'ApplePay' | 'Tamara' | 'Tabby' | 'Prepaid';
export interface DriverInfo {
  name: string;
  phone: string;
  vehiclePlate: string;
  rating: number;
  avatar?: string;
}
export interface HistoryEvent {
  id: string;
  timestamp: string;
  status: OrderStatus;
  message: string;
  actor: 'Pharmacist' | 'System' | 'Driver' | 'Customer Service';
  actorName: string;
}
export interface BranchRouting {
  branchName: string;
  timestamp: string;
  reason?: string;
}
export interface Order {
  id: string;
  customerName: string;
  itemsCount: number;
  totalAmount: number;
  status: OrderStatus;
  channel: Channel;
  paymentType: PaymentType;
  createdAt: string;
  slaMinutes: number;
  priority: 'High' | 'Medium' | 'Low';
  rejectionReason?: string;
  holdReason?: string;
  driverInfo?: DriverInfo;
  courierName?: string;
  courierPhone?: string;
  discountAmount?: number;
  couponCode?: string;
  customerAddress?: string;
  customerPhone?: string;
  // New Detailed Fields
  customerCity?: string;
  customerDistrict?: string;
  customerAge?: number;
  customerGender?: 'Male' | 'Female';
  isLoyaltyCustomer?: boolean;
  loyaltyPointsUsed?: number;
  qitafAmount?: number;
  walletAmount?: number;
  // ---
  isPickup?: boolean;
  otp?: string;
  otpVerified?: boolean;
  sourceOrderId?: string;
  previousStatus?: OrderStatus;
  requiresPrescription?: boolean;
  prescriptionVerified?: boolean;
  prescriptionImage?: string;
  routingHistory?: BranchRouting[];
  currentBranch?: string;
  history: HistoryEvent[];
}
interface OrderQueueProps {
  orders: Order[];
  onSelectOrder: (order: Order) => void;
}
export function OrderQueue({
  orders,
  onSelectOrder
}: OrderQueueProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [phoneQuery, setPhoneQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState('');
  const filteredOrders = orders.filter(order => {
    // Exclude terminal statuses from the active queue
    const isTerminalStatus = ['Delivered', 'Cancelled', 'Rejected'].includes(order.status);
    if (isTerminalStatus) return false;
    const matchesSearch = order.id.toLowerCase().includes(searchQuery.toLowerCase()) || order.customerName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPhone = order.customerPhone?.includes(phoneQuery) || !phoneQuery;
    const matchesSource = sourceFilter === 'all' || order.channel === sourceFilter;
    const matchesDate = !dateFilter || order.createdAt.includes(dateFilter);
    return matchesSearch && matchesPhone && matchesSource && matchesDate;
  });
  return <div className="space-y-6" data-id="element-550" data-mp="ab9sslpfl0zk">
      <div className="flex items-center justify-between" data-id="element-551" data-mp="aaibmfwkz5st">
        <div data-id="element-552" data-mp="68wqy8dt1n82">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-553" data-mp="6ahirq8qis28">Order Queue</h2>
          <p className="text-slate-500" data-id="element-554" data-mp="1ig8uaz1hdfd">
            Manage incoming orders and fulfillment SLAs
          </p>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm grid grid-cols-4 gap-4" data-id="element-555" data-mp="3smwv7ajdfib2">
        <div className="space-y-2" data-id="element-556" data-mp="u0u1zx9fkb84">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-557" data-mp="p80ux2qhoqsv">
            <Hash className="w-3 h-3" data-id="element-558" data-mp="8rqaz3s1j7sg" />
            Order ID / Name
          </label>
          <div className="relative" data-id="element-559" data-mp="ds4mivsuozv8">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-560" data-mp="dt5zj5tffwfl" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search ID or Name..." className="w-full pl-10 pr-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2" data-id="element-561" data-mp="3vzbxv546umz3" />
          </div>
        </div>

        <div className="space-y-2" data-id="element-562" data-mp="iqpfca5rwh9n">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-563" data-mp="npsjlsdxijgk">
            <Phone className="w-3 h-3" data-id="element-564" data-mp="1bfd2ycdzviqd" />
            Customer Phone
          </label>
          <div className="relative" data-id="element-565" data-mp="1g639vu22jtn6">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-566" data-mp="1g74uoq8vzui3" />
            <input type="text" value={phoneQuery} onChange={e => setPhoneQuery(e.target.value)} placeholder="050..." className="w-full pl-10 pr-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2" data-id="element-567" data-mp="1pju6zi4dkpef" />
          </div>
        </div>

        <div className="space-y-2" data-id="element-568" data-mp="16nc547vtb94g">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-569" data-mp="16odjal84kbqe">
            <Globe className="w-3 h-3" data-id="element-570" data-mp="12n22n578b0uc" />
            Source
          </label>
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="w-full px-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2 appearance-none" data-id="element-571" data-mp="1gls1ap9p58kt">
            <option value="all" data-id="element-572" data-mp="2n2n8tio3s6it">All Sources</option>
            <option value="Website" data-id="element-573" data-mp="2v2087lmxo4nt">Website</option>
            <option value="Aggregator" data-id="element-574" data-mp="2v3bfvwji1wk7">Aggregator</option>
          </select>
        </div>

        <div className="space-y-2" data-id="element-575" data-mp="2z4wx1u32gx07">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-576" data-mp="3426lp7imt4mz">
            <Calendar className="w-3 h-3" data-id="element-577" data-mp="3919oelb52yrs" />
            Date
          </label>
          <input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="w-full px-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2" data-id="element-578" data-mp="2bdj3xlx3cxe6" />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-579" data-mp="2q3742jtjdq0b">
        <div className="grid grid-cols-[1fr_1.5fr_1fr_1fr_1.5fr_1fr_1fr_auto] gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-580" data-mp="1slqc3utrdi81">
          <div data-id="element-581" data-mp="1q2lkrw1t6mbb">Order ID</div>
          <div data-id="element-582" data-mp="1q1tosk4aumki">Customer</div>
          <div data-id="element-583" data-mp="1pzzcfu73g2s1">Source</div>
          <div data-id="element-584" data-mp="1l2pgrwt42uvr">Status</div>
          <div data-id="element-585" data-mp="1l4k1pb4dumzu">Shipping / Driver</div>
          <div data-id="element-586" data-mp="1l1ec7urudj3l">SLA</div>
          <div data-id="element-587" data-mp="1g3w0xxqcqrsf">Amount</div>
          <div className="w-10" data-id="element-588" data-mp="1g34gnj27gghg"></div>
        </div>

        <div className="divide-y divide-slate-100" data-id="element-589" data-mp="1g5ywsye6rf52">
          {filteredOrders.map(order => <button key={order.id} onClick={() => onSelectOrder(order)} className={cn('w-full grid grid-cols-[1fr_1.5fr_1fr_1fr_1.5fr_1fr_1fr_auto] gap-4 px-6 py-5 items-center transition-colors text-left group', order.slaMinutes < 10 ? 'bg-rose-50/50 hover:bg-rose-50' : 'hover:bg-slate-50')} data-id="element-590" data-mp="1b7f9ounuvxb4">
              <div className="font-mono font-bold text-blue-600" data-id="element-591" data-mp="nfqft9vdz0s2">
                #{order.id}
              </div>

              <div data-id="element-592" data-mp="jf6hw42znt4v">
                <p className="font-semibold text-slate-900" data-id="element-593" data-mp="jeoodf7pnt0n">
                  {order.customerName}
                </p>
                <p className="text-xs text-slate-500" data-id="element-594" data-mp="278qj313m24zi">
                  {order.itemsCount} items
                </p>
              </div>

              <div className="flex items-center gap-2" data-id="element-595" data-mp="1xd6pilciridx">
                {order.channel === 'Website' ? <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-md text-[10px] font-bold uppercase" data-id="element-596" data-mp="22agmcm28mwbs">
                    <Globe className="w-3 h-3" data-id="element-597" data-mp="1oi0g7ce1ii6n" />
                    Website
                  </div> : <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50 text-orange-700 rounded-md text-[10px] font-bold uppercase" data-id="element-598" data-mp="1em5s41r1ruwh">
                    <Smartphone className="w-3 h-3" data-id="element-599" data-mp="1jls64dep9f6c" />
                    Aggregator
                  </div>}
              </div>

              <div data-id="element-600" data-mp="19mt5ni438puf">
                <StatusBadge status={order.status} data-id="element-601" data-mp="19ncbi0ggrns3" />
              </div>

              <div data-id="element-602" data-mp="19onjkcgxzhp0">
                {order.isPickup ? <div className="inline-flex items-center gap-1.5 rounded-md bg-purple-50 px-2 py-1 text-[10px] font-bold uppercase text-purple-700" data-id="element-603" data-mp="qzbpi1ymx5hq">
                    <User className="h-3 w-3" data-id="element-604" data-mp="zyypqkfmo597" />
                    Self Pickup
                  </div> : order.courierName || order.driverInfo ? <div className="space-y-1" data-id="element-605" data-mp="uysteqyexuks">
                    {order.courierName && <div className="flex flex-col gap-0.5" data-id="element-606" data-mp="19vjvj7zh1ke3">
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-600 uppercase" data-id="element-607" data-mp="14z35u9cmixyw">
                          <Globe className="w-3 h-3 text-blue-500" data-id="element-608" data-mp="1ioertc6yo5rq" />
                          {order.courierName}
                        </div>
                        {order.courierPhone && <div className="flex items-center gap-1.5 text-[9px] font-medium text-slate-400 pl-4" data-id="element-609" data-mp="1soxgck9h0afz">
                            <Phone className="w-2.5 h-2.5" data-id="element-610" data-mp="1nqchop9v9ytn" />
                            {order.courierPhone}
                          </div>}
                      </div>}
                    {order.driverInfo && <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-600 uppercase" data-id="element-611" data-mp="2thp54rrmsn08">
                        <User className="w-3 h-3 text-emerald-500" data-id="element-612" data-mp="2ojx67dskk0mj" />
                        {order.driverInfo.name}
                      </div>}
                  </div> : <span className="text-[10px] font-bold text-slate-300 uppercase italic" data-id="element-613" data-mp="3h8uin0a3n37d">
                    Not Assigned
                  </span>}
              </div>

              <div className="flex items-center gap-2" data-id="element-614" data-mp="37aesaziwbxyx">
                <div className={cn('flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold uppercase', order.slaMinutes < 10 ? 'bg-red-50 text-red-700' : order.slaMinutes < 20 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700')} data-id="element-615" data-mp="37bzmgo1atew9">
                  <Clock className="w-3 h-3" data-id="element-616" data-mp="1vno73hptoco7" />
                  {order.slaMinutes}m
                </div>
              </div>

              <div data-id="element-617" data-mp="20gqraz8pm9rq">
                <p className="font-bold text-slate-900" data-id="element-618" data-mp="20h8xanl6c2cu">
                  {order.totalAmount.toFixed(2)} SAR
                </p>
                <p className={cn('text-[10px] font-bold uppercase', order.paymentType === 'COD' ? 'text-amber-600' : 'text-emerald-600')} data-id="element-619" data-mp="29fkux9kgk0es">
                  {order.paymentType}
                </p>
              </div>

              <div className="flex items-center gap-3" data-id="element-620" data-mp="k38zpj9swosr">
                {order.requiresPrescription && <div className={cn('w-2 h-2 rounded-full', order.prescriptionVerified ? 'bg-emerald-500' : 'bg-purple-500 animate-pulse')} title={order.prescriptionVerified ? 'Prescription Verified' : 'Prescription Verification Required'} data-id="element-621" data-mp="p23s9cxklrq2" />}
                <div className="text-slate-300 group-hover:text-blue-600 transition-colors" data-id="element-622" data-mp="uzlz6unlx6xf">
                  <ChevronRight className="w-5 h-5" data-id="element-623" data-mp="19nz0m75grhzb" />
                </div>
              </div>
            </button>)}
        </div>
      </div>
    </div>;
}
function StatusBadge({
  status
}: {
  status: OrderStatus;
}) {
  const styles: Record<OrderStatus, string> = {
    Pending: 'bg-blue-50 text-blue-700 border-blue-100',
    Accepted: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    Picking: 'bg-amber-50 text-amber-700 border-amber-100',
    'Searching for Driver': 'bg-sky-50 text-sky-700 border-sky-100 animate-pulse',
    'Driver Assigned': 'bg-violet-50 text-violet-700 border-violet-100',
    'Out for Delivery': 'bg-orange-50 text-orange-700 border-orange-100',
    Delivered: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    'Delivery Attempt Failed': 'bg-rose-50 text-rose-700 border-rose-100',
    Held: 'bg-slate-100 text-slate-700 border-slate-200',
    Cancelled: 'bg-red-50 text-red-700 border-red-100',
    'Ready for Pickup': 'bg-purple-50 text-purple-700 border-purple-100',
    'Return Requested': 'bg-rose-50 text-rose-700 border-rose-100',
    'Waiting Return Confirmation': 'bg-amber-50 text-amber-700 border-amber-100',
    Refunded: 'bg-slate-50 text-slate-700 border-slate-100'
  };
  const icons: Record<OrderStatus, any> = {
    Pending: AlertCircle,
    Accepted: CheckCircle2,
    Picking: Package,
    'Searching for Driver': Truck,
    'Driver Assigned': User,
    'Out for Delivery': Truck,
    Delivered: CheckCircle2,
    'Delivery Attempt Failed': AlertCircle,
    Held: Clock,
    Cancelled: XCircle,
    'Ready for Pickup': Package,
    'Return Requested': AlertCircle,
    'Waiting Return Confirmation': Clock,
    Refunded: CheckCircle2
  };
  const Icon = icons[status] || AlertCircle;
  return <div className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border', styles[status])} data-id="element-624" data-mp="1p59z2cs4xsmy">
      <Icon className="w-3 h-3" data-id="element-625" data-mp="1f7n4khycrshv" />
      {status}
    </div>;
}